import { ADD_ON_IDS, FE_ADD_ON_ID } from '@ctct/domain-layer';
import type { Rating } from '@ctct/domain-layer';
import { NumberFormat } from '@ctct/infrastructure-layer';

import { SharedOrderItemsTranslationService } from './SharedOrderItemsTranslationService';
import type { TranslationGetter } from '../../../types';
import { formatTodaysTotal } from '../../_shared/OrderSummary/utils/OrderTotal';
import { orderSummaryEventBus } from '../OrderSummaryEventBus';
import type { FooterRow, OrderItemsService, OrderSummaryPageStore } from '../types';

export interface TrialerOrderItemsServiceParams {
  pageStore: OrderSummaryPageStore;
  t: TranslationGetter;
  rating: Rating | undefined;
  hideEditAddOns: boolean;
  isSmsBundle: boolean;
  isMobile: boolean;
  isNonProfit: boolean | undefined;
  isSocialStarter: boolean;
  isStripeCustomer: boolean;
  hideContactsTierLabel: boolean;
}

export class TrialerOrderItemsService implements OrderItemsService {
  private pageStore: OrderSummaryPageStore;

  private t: TranslationGetter;

  private rating: Rating | undefined;

  private hideEditAddOns: boolean;

  private isSmsBundle: boolean;

  private isMobile: boolean;

  private isSocialStarter: boolean;

  private hideContactsTierLabel: boolean;

  private sharedTranslationService: SharedOrderItemsTranslationService;

  constructor({
    pageStore,
    t,
    rating,
    hideEditAddOns,
    isSmsBundle,
    isMobile,
    isNonProfit,
    isSocialStarter,
    isStripeCustomer,
    hideContactsTierLabel,
  }: TrialerOrderItemsServiceParams) {
    this.pageStore = pageStore;
    this.t = t;
    this.rating = rating;
    this.hideEditAddOns = hideEditAddOns;
    this.isSmsBundle = isSmsBundle;
    this.isMobile = isMobile;
    this.isSocialStarter = isSocialStarter;
    this.hideContactsTierLabel = hideContactsTierLabel;
    this.sharedTranslationService = new SharedOrderItemsTranslationService(t, isStripeCustomer, isNonProfit);
  }

  public api() {
    return {
      addXeroAddon: () => {
        this.pageStore.setState({ isXeroAddonSelected: true });
      },
      removeXeroAddon: () => {
        this.pageStore.setState({ isXeroAddonSelected: false });
      },
      getIsXeroAddonSelected: () => {
        return this.pageStore.getState().isXeroAddonSelected;
      },
    };
  }

  public data() {
    return {
      getCopies: () => {
        return {
          heading: this.t('application-layer.order-summary.heading'),
          loadSpinner: this.t('application-layer.order-summary.load-spinner'),
        };
      },

      getOrderRows: (isXeroAddonSelected?: boolean) => {
        if (!this.rating) {
          return [];
        }

        const planRow = this.getPlanRow(this.rating);
        const addOnRows = this.getAddOnRows(this.rating, isXeroAddonSelected);

        return [planRow, ...addOnRows];
      },

      getFooterRows: ({
        isVatEu,
        isB2bNotificationVisible,
      }: {
        isVatEu: boolean;
        isB2bNotificationVisible: boolean;
      }): FooterRow[] => {
        if (!this.rating) {
          return [];
        }

        const subtotalBeforeTaxRow = this.getSubtotalBeforeTaxRow(this.rating);
        const discountRows = this.getDiscountRows(this.rating);
        const taxRow = this.getTaxRow(this.rating, isVatEu, isB2bNotificationVisible);
        const todayTotalRow = this.getTodaysTotalRow(this.rating);
        const totalSavingsRow = this.getTotalSavingsRow(this.rating);

        return [subtotalBeforeTaxRow, ...discountRows, taxRow, todayTotalRow, totalSavingsRow].flatMap((row) =>
          row ? [row] : []
        );
      },

      getSubfooterRows: () => {
        return [];
      },

      getTodaysTotal: () => {
        return formatTodaysTotal(this.rating);
      },
    };
  }

  public handlers() {
    return {
      onRemoveAddOnClick: (addOnId: string) => {
        orderSummaryEventBus.emit('addOnRemoved', addOnId, { source: 'user' });
      },

      onEditAddOnClick: (addOnId: string) => {
        orderSummaryEventBus.emit('editAddOnClicked', addOnId, { source: 'user' });
      },

      onEditSmsBundleTierClick: () => {
        orderSummaryEventBus.emit('smsBundleTierEditClicked', { source: 'user' });
      },

      onEditPlanClick: (planId: string) => {
        orderSummaryEventBus.emit('editPlanClicked', planId, { source: 'user' });
      },
    };
  }

  private getPlanRow(rating: Rating) {
    const handlers = this.handlers();
    const plan = rating.getPlan();
    const hasDiscount = rating.getPlanPrepayDiscount().priceAmount > 0;

    return {
      id: String(plan.planId),
      variant: 'bold' as const,
      mainLabel: rating.getPlanName(),
      secondaryLabels: this.getPlanSecondaryLabels(rating, plan),
      priceLabel: rating.getPlanBaseTotal().format(rating.getCurrency()),
      buttons: [
        {
          label: this.t('application-layer.order-summary.edit-plan'),
          onClick: () => handlers.onEditPlanClick(String(plan.planId)),
          disabled: this.pageStore.getState().orderSummaryDisabled,
        },
      ],
      ...(hasDiscount
        ? {
            discountLabel: this.t(this.sharedTranslationService.getSavingsDiscountKey()),
            discountPriceLabel: `-${rating.getPlanPrepayDiscount().format(rating.getCurrency())}`,
          }
        : {}),
    };
  }

  private getAddOnRows(rating: Rating, isXeroAddonSelected?: boolean) {
    const handlers = this.handlers();
    const addons = rating.getAddons();

    const generateButtons = (addonId: string) => {
      const removeAddonButton = {
        label: this.t('application-layer.order-summary.remove'),
        onClick: () => handlers.onRemoveAddOnClick(addonId),
        disabled: this.pageStore.getState().orderSummaryDisabled,
      };

      // SMS bundle: SMS add-on retains Remove (both viewports) and Edit (mobile only,
      // which opens the SMS tier modal); non-SMS add-ons stay button-less so the user
      // can only adjust them via the inline configurator.
      if (this.isSmsBundle) {
        if (addonId !== ADD_ON_IDS.SMS) {
          return [];
        }

        if (!this.isMobile) {
          return [removeAddonButton];
        }

        const editSmsBundleTierButton = {
          label: this.t('application-layer.order-summary.edit-addon'),
          onClick: () => handlers.onEditSmsBundleTierClick(),
          disabled: this.pageStore.getState().orderSummaryDisabled,
        };

        return [editSmsBundleTierButton, removeAddonButton];
      }

      const editAddonButton = {
        label: this.t('application-layer.order-summary.edit-addon'),
        onClick: () => handlers.onEditAddOnClick(addonId),
        disabled: this.pageStore.getState().orderSummaryDisabled,
      };

      const buttonsForAddons = this.hideEditAddOns ? [removeAddonButton] : [editAddonButton, removeAddonButton];

      return buttonsForAddons;
    };

    const ratingCallAddons = (addons || []).map((addon) => {
      const addonHasDiscount = addon.monthlyDiscount.priceAmount > 0;

      return {
        id: addon.id,
        mainLabel: this.getAddOnMainLabel(addon),
        secondaryLabels: this.getAddonSecondaryLabels(rating, addon),
        priceLabel: String(addon.fullPrice.format(rating.getCurrency())),
        buttons: this.isFreeSmsAddon(addon) ? [] : generateButtons(addon.id),
        ...(addonHasDiscount
          ? {
              discountLabel: this.t(this.sharedTranslationService.getSavingsDiscountKey()),
              discountPriceLabel: this.getAddOnDiscountPriceLabel(rating, addon),
            }
          : {}),
      };
    });

    const xeroAddon = {
      id: FE_ADD_ON_ID.XERO,
      mainLabel: this.t('application-layer.order-summary.xero-addon-label'),
      secondaryLabels: [],
      priceLabel: this.t('application-layer.order-summary.xero-addon-price'),
      buttons: generateButtons(FE_ADD_ON_ID.XERO),
    };

    return isXeroAddonSelected ? [...ratingCallAddons, xeroAddon] : ratingCallAddons;
  }

  private getSubtotalBeforeTaxRow(rating: Rating) {
    const priceLabel = rating.getPlanTotal().add(rating.getAddOnsTotal()).format(rating.getCurrency());

    return {
      id: 'subtotal-before-tax',
      label: this.t('application-layer.order-summary.subtotal-before-tax'),
      priceLabel,
    };
  }

  private getTaxRow(rating: Rating, isVatEu: boolean, isB2bNotificationVisible: boolean): FooterRow {
    const priceLabel = rating.getTotalTax().format(rating.getCurrency());

    return {
      id: 'estimated-taxes',
      label: isVatEu
        ? this.t('application-layer.order-summary.vat-zero-percent')
        : this.t('application-layer.order-summary.estimated-taxes'),
      priceLabel,
      tooltipI18nKey: isB2bNotificationVisible ? 'application-layer.order-summary.tax-b2b-tooltip' : undefined,
    };
  }

  private getTodaysTotalRow(rating: Rating): FooterRow {
    return {
      id: 'todays-total',
      variant: 'bold' as const,
      label: this.t('application-layer.order-summary.todays-total'),
      priceLabel: formatTodaysTotal(rating),
    };
  }

  private getTotalSavingsRow(rating: Rating): FooterRow | null {
    const hasSavings = rating.hasSavings();

    if (!hasSavings) {
      return null;
    }

    return {
      id: 'total-savings',
      variant: 'summary' as const,
      label: '',
      priceLabel: this.t('application-layer.order-summary.total-savings', {
        total: rating.getSavingsTotal().format(rating.getCurrency()),
      }),
    };
  }

  private getDiscountRows(rating: Rating) {
    return rating
      .getNewAppliedDiscountCodes()
      .filter((promoCodeVO) => {
        // Filter out non-discount promo codes (promotionCredit === 0)
        return !promoCodeVO.isNonDiscount();
      })
      .map((promoCodeVO) => {
        const promotionValue = promoCodeVO.getPromotionCredit().format(rating.getCurrency());
        const matchingAddon = rating.getAddons().find((addOn) => addOn.id === promoCodeVO.getBaasServiceId());

        // for addons we show it's name, for plan we show "your order"
        const productName = matchingAddon?.name || this.t('application-layer.order-summary.your-order');

        return {
          id: promoCodeVO.getCode(),
          variant: 'discount' as const,
          label: this.t('application-layer.order-summary.discount-label', { promotionValue, productName }),
          priceLabel: `-${promotionValue}`,
        };
      });
  }

  private getPlanSecondaryLabels(rating: Rating, plan) {
    const periodLabel = this.sharedTranslationService.getPeriodLabel(rating);

    const price = rating.getPlanBaseMonthly().format(rating.getCurrency());
    const period = rating.getPaymentPeriod();
    const { tierMin, tierMax } = plan.currentTier;
    const labels: string[] = [];

    if (!this.isSocialStarter && !this.hideContactsTierLabel) {
      labels.push(this.sharedTranslationService.getContactsTierLabel(tierMin, tierMax));
    }

    labels.push(
      this.t('application-layer.order-summary.period-plan-secondary-label', {
        periodLabel,
        price,
        period,
      })
    );

    return labels;
  }

  private getAddOnMainLabel(addon: ReturnType<Rating['getAddons']>[0]) {
    if (addon.id !== 'CTCT-SMS-ADDON') {
      return addon.name;
    }

    const FREE_SMS_AMOUNT_IN_PREMIUM_PLAN = '500';
    const smsNumber = this.isFreeSmsAddon(addon)
      ? FREE_SMS_AMOUNT_IN_PREMIUM_PLAN
      : NumberFormat.formatWithThousandsSeparator(addon.usage?.used || 0);

    return this.t('application-layer.order-summary.sms-addon-label', { smsNumber });
  }

  private isFreeSmsAddon(addon: ReturnType<Rating['getAddons']>[0]) {
    const freeSmsTierFromPremium =
      addon.id === ADD_ON_IDS.SMS &&
      addon.fullPrice.priceAmount + addon.listPrice.priceAmount + addon.monthlyCost.priceAmount === 0;

    return freeSmsTierFromPremium;
  }

  private getAddonSecondaryLabels(rating: Rating, addon: ReturnType<Rating['getAddons']>[0]) {
    return [
      this.t('application-layer.order-summary.addon-secondary-label', {
        periodLabel: this.sharedTranslationService.getPeriodLabel(rating),
        price: addon.listPrice.format(rating.getCurrency()),
        period: addon.period,
      }),
    ];
  }

  private getAddOnDiscountPriceLabel(rating: Rating, addon: ReturnType<Rating['getAddons']>[0]) {
    return `-${addon.monthlyDiscount.multiply(rating.getPaymentPeriod()).format(rating.getCurrency())}`;
  }
}
